'use client';

import { useEffect, useId, useState } from 'react';

import { AdaptiveDialog } from './adaptive-dialog';
import { Button } from './button';
import { Input } from './input';

interface ConfirmDialogProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	description: string;
	errorMessage?: string | null;
	confirmLabel?: string;
	confirmVariant?: 'solid-danger' | 'solid';
	onConfirm: () => void;
	isLoading?: boolean;
	loadingLabel?: string;
	/** Focus Cancel on open and keep keyboard navigation within this confirmation. */
	trapFocus?: boolean;
	/**
	 * Type-to-confirm gate for irreversible actions (e.g. deleting a space).
	 * When set, the confirm button stays disabled until the user types this
	 * phrase. Matching ignores case and surrounding / repeated whitespace.
	 */
	confirmPhrase?: string;
}

const normalizePhrase = (value: string) =>
	value.trim().replace(/\s+/g, ' ').toLowerCase();

export const ConfirmDialog = ({
	isOpen,
	onClose,
	title,
	description,
	errorMessage,
	confirmLabel = 'Confirm',
	confirmVariant = 'solid-danger',
	onConfirm,
	isLoading = false,
	loadingLabel,
	trapFocus,
	confirmPhrase,
}: ConfirmDialogProps) => {
	const inputId = useId();
	const [typed, setTyped] = useState('');

	// Every open starts from an empty field — a phrase typed for one space
	// must never carry over to the next confirmation.
	useEffect(() => {
		if (isOpen) setTyped('');
	}, [isOpen]);

	const phraseMatches =
		!confirmPhrase || normalizePhrase(typed) === normalizePhrase(confirmPhrase);
	const canConfirm = phraseMatches && !isLoading;

	return (
		<AdaptiveDialog
			open={isOpen}
			onClose={onClose}
			title={title}
			desktopClassName="w-[420px] max-w-[90vw]"
			showCloseButton={false}
			trapFocus={trapFocus}
		>
			<div className="p-6">
				<h3 className="m-0 mb-2 text-[18px] font-semibold text-neutral">
					{title}
				</h3>
				<p
					className={
						errorMessage || confirmPhrase
							? 'm-0 mb-3 text-[14px] text-neutral-muted'
							: 'm-0 mb-5 text-[14px] text-neutral-muted'
					}
				>
					{description}
				</p>
				{confirmPhrase && (
					<div className="mb-5 flex flex-col gap-2">
						<label
							htmlFor={inputId}
							className="text-[13px] text-neutral-muted"
						>
							Type{' '}
							<span className="font-semibold text-neutral">
								{confirmPhrase}
							</span>{' '}
							to confirm.
						</label>
						<Input
							id={inputId}
							inputSize="sm"
							value={typed}
							onChange={(e) => setTyped(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && canConfirm) onConfirm();
							}}
							placeholder={confirmPhrase}
							autoComplete="off"
							autoFocus
							disabled={isLoading}
						/>
					</div>
				)}
				{errorMessage && (
					<p className="m-0 mb-5 text-[13px] text-[var(--color-danger)]">
						{errorMessage}
					</p>
				)}
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={onClose}
						disabled={isLoading}
					>
						Cancel
					</Button>
					<Button
						variant={confirmVariant}
						size="sm"
						onClick={onConfirm}
						disabled={!canConfirm}
					>
						{isLoading && loadingLabel ? loadingLabel : confirmLabel}
					</Button>
				</div>
			</div>
		</AdaptiveDialog>
	);
};
